import { logger } from '@marriott/mi-headless-utils';

const { log } = logger({})('retargetingDatalayerRecovery');

/**
 * Local-dev fallback for the datalayer route. Deployed environments remap the
 * renderer's API routes (e.g. /mi/api/homepage/datalayer on UAT/prod), so the
 * real path must come from the SSR-injected pageProps (CLIENT_DATA_LAYER_PATH).
 */
export const DATALAYER_API_PATH = '/api/datalayer';

/**
 * Resolve the datalayer endpoint the page itself is configured with. Read from
 * __NEXT_DATA__ pageProps (`datalayerUrl`, set by the preProcessor from
 * CLIENT_DATA_LAYER_PATH) — it is plain SSR config, so it is present even when
 * the datalayer network call itself failed. Falls back to the local-dev path.
 */
export function resolveDatalayerEndpoint(): string {
  if (typeof window !== 'undefined') {
    const nextData = (window as { __NEXT_DATA__?: { props?: { pageProps?: { datalayerUrl?: unknown } } } })
      .__NEXT_DATA__;
    const configured = nextData?.props?.pageProps?.datalayerUrl;
    if (typeof configured === 'string' && configured.trim()) return configured;
  }
  return DATALAYER_API_PATH;
}

/** ACDL event pushed by UserSessionProvider once the session datalayer merge completes. */
export const DATALAYER_LOADED_EVENT = 'dataLayerLoaded';

/**
 * Grace period before the recovery fetch. Gives the page's own bootstrap (SSR
 * datalayer + the provider's dataLayerLoaded push) time to deliver before the
 * card re-requests the datalayer itself.
 */
export const DATALAYER_RECOVERY_FETCH_DELAY_MS = 2500;

const DATALAYER_RECOVERY_FETCH_TIMEOUT_MS = 8000;

// Without the enableWebSDK query param the route responds with
// `var dataLayer = {...}; var mvpOffers = {...};` where the dataLayer literal
// is JSON.stringify output (getDatalayerResponse in mi-headless-utils).
const DATALAYER_SCRIPT_PATTERN = /var dataLayer = (\{[\s\S]*\});\s*var mvpOffers/;

export function parseDatalayerScript(script: string): Record<string, unknown> | null {
  const match = DATALAYER_SCRIPT_PATTERN.exec(script);
  if (!match) return null;
  try {
    const parsed: unknown = JSON.parse(match[1]);
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * One-shot client-side re-fetch of the session datalayer, for when the page's
 * initial delivery failed or stalled (an empty ACDL state for the whole page
 * view — the datalayer request is known to hang without a timeout). Best
 * effort: resolves null on any failure, callers keep their designed fallback.
 */
export async function fetchDatalayerSnapshot(): Promise<Record<string, unknown> | null> {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return null;
  const controller = typeof AbortController === 'function' ? new AbortController() : undefined;
  const timeoutId = controller
    ? window.setTimeout(() => controller.abort(), DATALAYER_RECOVERY_FETCH_TIMEOUT_MS)
    : undefined;
  try {
    const response = await fetch(resolveDatalayerEndpoint(), {
      credentials: 'same-origin',
      signal: controller?.signal,
    });
    if (!response.ok) {
      log.warn('[HOMEPAGE][RETARGETING][DATALAYER_RECOVERY_FAILED]', { status: response.status });
      return null;
    }
    const snapshot = parseDatalayerScript(await response.text());
    if (!snapshot) {
      log.warn('[HOMEPAGE][RETARGETING][DATALAYER_RECOVERY_FAILED]', { status: 'unparseable' });
    }
    return snapshot;
  } catch (error) {
    log.warn('[HOMEPAGE][RETARGETING][DATALAYER_RECOVERY_FAILED]', {
      errorType: error instanceof Error ? error.name : typeof error,
    });
    return null;
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }
}
